import moment from 'moment';
import _ from 'lodash';
import { Calculator } from './Calculator';
import { countries, entryStockOptionVar, limitPricing } from '../../config';
import { t } from '../../components/common/Translate';

export const extractRegistrationPlate = (record) => record?.vehicle?.registration.replace(/[^a-zA-Z^0-9]/g, '');

export const extractVin = (record) => record?.vehicle?.vin;

export const extractCountry = (record) => {
  const { countryId } = record.record;
  return countries[countryId];
};

export const extractEntryDateRelease = (record) => {
  const { entryRegistrationDate, registrationDate } = record.vehicle;
  return entryRegistrationDate ?? registrationDate;
};

function getDateFormat(date, format) {
  if (!date) return null;
  try {
    const res = moment(date).format(format);
    if (format === 'YYYY' && _.inRange(res, 1950, 2100)) return res;
    if (format === 'MM') return res;
    return null;
  } catch (e) {
    return null;
  }
}

export const extractVehData = (record) => {
  const versionId = [0, null].includes(_.get(record, 'valuation.versionId', null))
    ? _.get(record, 'vehicle.versionId', null)
    : _.get(record, 'valuation.versionId');
  const { vehicle } = record;
  const mileage = _.get(vehicle, 'mileage', null);
  const registrationDate = _.get(vehicle, 'registrationDate.date', null);
  const carcheckRefurbishingCosts = _.round(_.get(record, 'expertiseCosts.mdCosts', null));
  const yearMec = getDateFormat(registrationDate, 'YYYY');
  const monthMec = getDateFormat(registrationDate, 'MM');
  const carProfile = _.get(record, 'record.profile', null);
  const authorization = _.get(record, 'supervisor.authorization', false);
  const c2bOffer = _.get(record, 'supervisor.c2bOffer', null);
  const { countryId } = record.record;
  const country = countries[countryId];
  const comment = _.get(record, 'supervisor.comment', null);
  const rotation = _.get(record, 'valuation.rotation', null);

  const makeId = _.get(vehicle, 'brandId', null);
  const modelId = _.get(vehicle, 'modelId', null);
  const fuelId = _.get(vehicle, 'fuelId', null);
  const bodyId = _.get(vehicle, 'bodyId', null);
  const doors = _.get(vehicle, 'door', null);
  const gearId = _.get(vehicle, 'gearBoxId', null);
  const dateOfRelease = _.get(vehicle, 'registrationDate.date', null);
  const kw = _.get(vehicle, 'kw', null);
  const engine = _.get(vehicle, 'ps', null);
  const seats = _.get(vehicle, 'seats', null);
  const cylinderCapacity = _.get(vehicle, 'cylinderCapacity', null);
  const liter = _.get(vehicle, 'liter', null);
  const entryDateOfRelease = _.get(vehicle, 'entryRegistrationDate.date', null);
  return {
    versionId,
    mileage,
    yearMec,
    monthMec,
    country,
    carcheckRefurbishingCosts,
    carProfile,
    authorization,
    c2bOffer,
    comment,
    rotation,
    makeId,
    modelId,
    fuelId,
    bodyId,
    doors,
    seats,
    gearId,
    dateOfRelease,
    kw,
    engine,
    cylinderCapacity,
    liter,
    entryDateOfRelease,
  };
};

export const calculateTradeIn = ({ planedDelay, marketValue, country, refurbishingCosts, preferences }) => {
  const calculator = new Calculator({ planedDelay, marketValue, country, refurbishingCosts, preferences });
  return calculator.getTradeIn();
};

export const calculateEntryStockResult = (tradeInResult, entryStockValue) => {
  if (entryStockValue === 'selected') return tradeInResult;
  return (
    _.round(_.subtract(tradeInResult, (tradeInResult * (0.75 * (parseInt(entryStockValue, 10) / 15))) / 100) / 50) * 50
  );
};

export const calculatePricingResult = (tradeIn, frevo) => _.round(_.subtract(tradeIn, frevo) / 50) * 50;

export const calculatePrixEt = (tradeIn, frevo, vat, country) => {
  const calculator = new Calculator({ country });
  const trad = _.round(_.subtract(tradeIn - frevo) / 50) * 50;
  let price;
  if (vat === 'yes_50percent') {
    const res = (trad / calculator.getVat()) * 1.105;
    price = _.round(res / 50) * 50;
  }
  if (vat === 'yes_100percent') price = _.round(trad / calculator.getVat() / 50) * 50;
  return price > 0 ? price : 0;
};

export const extractIdentification = (record) => record?.vehicle?.identification;

export const formatEntryStocksDays = (data) => {
  const lists = [];
  data.forEach((i) =>
    Object.entries(i).forEach(([label, value]) => {
      lists[label] = `${t(entryStockOptionVar[label])} (${moment(value).format('DD/MM/YYYY')})`;
    }),
  );
  return lists;
};

export const checkIfSelectedDateExist = (lists, entryStockKey, entryStockdate) => {
  let res = false;
  lists.forEach((i) => {
    Object.keys(i).forEach((key) => {
      if (parseInt(key, 10) === entryStockKey && moment(i[key]).format('DD/MM/YYYY') === entryStockdate) res = true;
    });
  });
  return res;
};

export const generateEntryStockOption = (isNrm) => {
  const lists = [];
  const val = isNrm ? 240 : 180;
  let option = {};
  const now = moment();
  let i = 0;
  while (i <= val) {
    if (i === 0) option[i] = now.format('YYYY/MM/DD');
    else option[i] = moment().add(i, 'days').format('YYYY/MM/DD');
    lists.push(option);
    option = {};
    i += 15;
  }
  return lists;
};

export const calculateMinimValuePricing = (sourceId = 2, value) => {
  let newValue = value;
  if (limitPricing[sourceId] && newValue < limitPricing[sourceId]) {
    newValue = limitPricing[sourceId];
  } else if (newValue < limitPricing.default) {
    newValue = limitPricing.default;
  }
  return newValue;
};

export const processDate = (date) => {
  const parts = date.split('/');
  return new Date(parts[2], parts[1] - 1, parts[0]);
};

export const calculateMyValuationEt = (tradeIn, vat) => {
  let price;
  if (vat === 'yes_50percent') {
    price = Math.round(tradeIn / 1.105);
  }
  if (vat === 'yes_100percent') price = Math.round(tradeIn / 1.21);
  return price > 0 ? price : 0;
};

// function extractPricingData(record){

//   params.correctedPrice = JSON.parse(localStorage.getItem(`${getCookie('userId')}.${recordId}.corrected`));

//   params.rotation = JSON.parse(localStorage.getItem(`${getCookie('userId')}.${recordId}.planedDelay`));
//   params.refurbishmentCost = parseInt(JSON.parse(localStorage.getItem(`${getCookie('userId')}.${recordId}.frevo`)));
//   params.tradeIn = _.round(JSON.parse(localStorage.getItem(`${getCookie('userId')}.${recordId}.tradeIn`)));
// }
